import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldAlert, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  self_harm: 'Self-Harm Signal',
  abuse_disclosure: 'Abuse Disclosure',
  acute_mental_health: 'Acute Mental Health Crisis',
  medical_emergency: 'Medical Emergency',
};

export default function SafetyEscalationsPage({ user, onLogout }) {
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackingId, setAckingId] = useState(null);
  const isCoach = user.role === 'head_coach' || user.role === 'assistant_coach';

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/safety-escalations`, {
        params: { user_id: user.id, team_id: user.team_id },
      });
      setEscalations(res.data);
    } catch (error) {
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        toast.error('Failed to load safety escalations');
      }
    } finally {
      setLoading(false);
    }
  }, [user.id, user.team_id]);

  useEffect(() => {
    if (isCoach) load();
    else setLoading(false);
  }, [isCoach, load]);

  const handleAcknowledge = async (escalationId) => {
    setAckingId(escalationId);
    try {
      await axios.post(`${API}/safety-escalations/${escalationId}/acknowledge`,
        { user_id: user.id },
        { params: { team_id: user.team_id } }
      );
      toast.success('Escalation acknowledged — you now own following up on this');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setAckingId(null);
    }
  };

  if (!isCoach) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8 flex items-center justify-center min-h-[60vh]" data-testid="safety-escalations-no-access">
          <Card className="max-w-md">
            <CardContent className="p-12 text-center space-y-4">
              <ShieldAlert className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-foreground">
                Access Restricted
              </h2>
              <p className="text-muted-foreground">
                Safety alerts are only available to coaches.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const openEscalations = escalations.filter((e) => e.status !== 'acknowledged');
  const acknowledgedEscalations = escalations.filter((e) => e.status === 'acknowledged');

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 md:p-8 space-y-6" data-testid="safety-escalations-page">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-foreground flex items-center gap-3">
            <ShieldAlert className="h-8 w-8 text-destructive" /> Safety Alerts
          </h1>
          <p className="text-muted-foreground mt-2">
            Automated escalations from player/parent chat messages that needed a trusted adult's attention.
          </p>
        </div>

        {loading ? (
          <div className="text-muted-foreground" data-testid="safety-escalations-loading">Loading...</div>
        ) : escalations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground" data-testid="safety-escalations-empty">
              No safety alerts for this team. That's a good thing.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {openEscalations.length > 0 && (
              <div className="space-y-3" data-testid="safety-escalations-open-section">
                <h2 className="text-base md:text-lg font-bold uppercase tracking-wide text-destructive">
                  Open — Needs Attention ({openEscalations.length})
                </h2>
                {openEscalations.map((e) => (
                  <EscalationCard key={e.id} escalation={e} onAcknowledge={handleAcknowledge} acking={ackingId === e.id} />
                ))}
              </div>
            )}
            {acknowledgedEscalations.length > 0 && (
              <div className="space-y-3" data-testid="safety-escalations-acknowledged-section">
                <h2 className="text-base md:text-lg font-bold uppercase tracking-wide text-muted-foreground">
                  Acknowledged ({acknowledgedEscalations.length})
                </h2>
                {acknowledgedEscalations.map((e) => (
                  <EscalationCard key={e.id} escalation={e} onAcknowledge={handleAcknowledge} acking={false} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function EscalationCard({ escalation: e, onAcknowledge, acking }) {
  const acknowledged = e.status === 'acknowledged';
  return (
    <Card className={acknowledged ? 'opacity-70' : 'border-destructive/50'} data-testid={`safety-escalation-card-${e.id}`}>
      <CardContent className="p-5 flex items-start justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="font-heading text-lg font-bold text-foreground">{e.player_name}</span>
            <Badge variant={acknowledged ? 'secondary' : 'destructive'} data-testid={`safety-escalation-category-${e.id}`}>
              {CATEGORY_LABELS[e.category] || e.category}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" /> Detected {new Date(e.detected_at).toLocaleString()}
            {e.triggering_role === 'parent' ? ' — from parent' : ' — from player'}
          </p>
          <p className="text-xs text-muted-foreground">
            Notified {new Set((e.notified || []).map((n) => n.destination)).size} coach(es) via SMS + email.
          </p>
          {acknowledged && (
            <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`safety-escalation-ack-info-${e.id}`}>
              <CheckCircle2 className="h-3 w-3 text-primary" /> Acknowledged {new Date(e.acknowledged_at).toLocaleString()}
            </p>
          )}
        </div>
        {!acknowledged && (
          <Button
            onClick={() => onAcknowledge(e.id)}
            disabled={acking}
            data-testid={`safety-escalation-acknowledge-btn-${e.id}`}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" /> {acking ? 'Acknowledging...' : "I've Got This"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
